
<template>
  <div v-if="!isImportMode" class="additional-option compact-table-controls">
    <div class="additional-option-row flex">
      <div class="additional-option-col flex">
        <button
            class="filters-toggle link-dotted-icon"
            type="button"
            :aria-expanded="filtersExpanded"
            :aria-controls="filtersContainerId"
            @click="toggleFilters"
        >
          <svg class="filters-toggle__icon" aria-hidden="true">
            <use :href="`/img/sprite.svg#${filtersExpanded ? 'svg-up' : 'svg-down'}`"></use>
          </svg>
          <span class="font-14">{{ t(filtersExpanded ? 'filters.hide' : 'filters.show') }}</span>
        </button>
        <a class="edit-option link-dotted-icon" @click="showEdit = true">
          <svg class="svg-edit">
            <use href="/img/sprite.svg#svg-edit"></use>
          </svg>
          <span class="font-14">{{ t('actions.editSelected') }}</span>
        </a>
      </div>
      <div class="additional-option-col flex">
        <a class="edit-option link-dotted-icon" @click="showSettings = true">
          <svg class="svg-edit">
            <use href="/img/sprite.svg#svg-settings"></use>
          </svg>
          <span class="font-14">{{ t('actions.columnSettings') }}</span>
        </a>
        <a v-if="mode=='order'" class="history-option link-dotted-icon" @click="showHistory = true">
          <svg class="svg-history">
            <use href="/img/sprite.svg#svg-history"></use>
          </svg>
          <span class="font-14">{{ t('actions.changeHistory') }}</span>
        </a>
        <a
          v-if="mode === 'order' && order.permissions?.can_read_messages"
          class="history-option link-dotted-icon"
          @click="$emit('show-message-history')"
        >
          <svg class="svg-history">
            <use href="/img/sprite.svg#svg-history"></use>
          </svg>
          <span class="font-14">{{ t('orderMessages.history') }}</span>
        </a>
        <a class="attach-option link-dotted-icon" @click="showAttachment = true">
          <svg class="svg-attach">
            <use href="/img/sprite.svg#svg-attach"></use>
          </svg>
          <span class="font-14">{{ t('actions.attachments') }}</span>
        </a>
      </div>
    </div>
  </div>

  <!--ФИЛЬТРЫ НАД ТАБЛИЦЕЙ-->
  <div
      v-if="!isImportMode"
      v-show="filtersExpanded"
      :id="filtersContainerId"
      :class="['filters-grid', 'border-filters', 'compact-filters', 'positions-filters', `positions-filters--${mode}`]"
  >
    <p class="muted-13" style="grid-column: 1 / -1">{{ t('shipmentImportPages.dateFilterHint') }}</p>
    <template v-for="filter in filtersConfig" :key="filter.key">
      <template v-if="filter.mode === 'order' || filter.mode === mode">
        <MultiSelectFilter
            v-if="filter.type === 'multi-select'"
            v-model="filtersState[filter.stateKey]"
            :label="t(filter.labelKey)"
            :options="filter.options"
            :placeholder="t('form.select')"
        />

        <CheckboxFilter
            v-else-if="filter.type === 'checkbox'"
            v-model="filtersState[filter.stateKey]"
            :class="{ 'positions-filter--arrived': filter.key === 'arrived' }"
            :label="t(filter.labelKey)"
        />

        <div v-else-if="filter.type === 'date'" class="form-item form-calendar span-1">
          <label class="calendar-label">{{ t(filter.labelKey) }}</label>

          <div class="calendar-field">
            <Datepicker
                v-model="orderDatePickerValue"
                :range="orderDateFilterMode === 'range'"
                class="calendar-input"
                :placeholder="t(filter.placeholderKey)"
                :enable-time-picker="false"
                auto-apply
                format="dd.MM.yyyy"
                :locale="locale"
            />
            <span class="icon-calendar"></span>
          </div>
        </div>

        <div v-else-if="filter.type === 'search'" class="filters-search span-1">
          <FieldSearchFilter
              v-model="filtersState[filter.stateKey]"
              :label="t(filter.labelKey)"
              :placeholder="t(filter.placeholderKey)"
              :fields="filter.fields"
          />
        </div>
      </template>
    </template>
  </div>

  <!--TABLE-->
  <div
      class="table-group allpositions"
      :class="{ 'allpositions--single-line': !isImportMode }"
      ref="tableGroup"
  >
    <div v-if="loading" class="table-loader table-loader--overlay"></div>
    <!-- Клон шапки для «прилипания» к верху окна -->
    <div class="table-sticky-header" v-if="stickyVisible" :style="stickyHeaderStyle">
      <div class="table-sticky-inner" :style="{ transform: `translateX(${-scrollLeft}px)` }">
        <table class="table editable-table has-special-drop"
               :style="{ width: stickyTableWidth + 'px' }">
          <colgroup>
            <col v-for="(w, i) in columnWidths" :key="i" :style="{ width: w + 'px' }" />
          </colgroup>
          <thead class="table-thead">
          <tr>
            <th v-if="!isImportMode" class="cell-choose">
              <label class="checkbox-label">
                <input type="checkbox" name="chbx_all_sticky" :checked="isAllSelected()"
                       @change="toggleSelectAll">
                <span class="fake-checkbox"></span>
              </label>
            </th>
            <th v-if="!isImportMode" class="cell-option"></th>

            <template v-for="column in visibleTableColumns" :key="column.key">
              <th
                  :class="[column.class, { 'cell-identifier': isIdentifierColumn(column.key) }]"
              >
                <template v-if="column.sortKey">
                  <button class="sort-btn" type="button" :title="getColumnLabel(column)" @click="setSort(column.sortKey)">
                    <span class="table-header-text">{{ getColumnLabel(column) }}</span>
                    <span class="sort-icons" aria-hidden="true">
                            <span :class="['sort-up', { active: sortKey === column.sortKey && sortDir === 'asc' }]"></span>
                            <span :class="['sort-down', { active: sortKey === column.sortKey && sortDir === 'desc' }]"></span>
                          </span>
                  </button>
                </template>
                <template v-else>
                  <span class="table-header-text" :title="getColumnLabel(column)">{{ getColumnLabel(column) }}</span>
                </template>
              </th>
            </template>
            <th v-if="!isImportMode" class="cell-options"></th>
          </tr>
          </thead>
        </table>
      </div>
    </div>

    <button
        v-if="showScrollLeft"
        class="table-button-prev"
        type="button"
        :style="floatingButtonStyle"
        @mouseenter="startAutoScroll('left')"
        @mouseleave="stopAutoScroll"
        @mousedown.prevent="startAutoScroll('left')"
        @mouseup="stopAutoScroll"
        @touchstart.prevent="startAutoScroll('left')"
        @touchend="stopAutoScroll"
    >
      <svg class="svg-left">
        <use href="/img/sprite.svg#svg-left"></use>
      </svg>
    </button>

    <button
        v-if="showScrollRight"
        class="table-button-next"
        type="button"
        :style="floatingButtonStyle"
        @mouseenter="startAutoScroll('right')"
        @mouseleave="stopAutoScroll"
        @mousedown.prevent="startAutoScroll('right')"
        @mouseup="stopAutoScroll"
        @touchstart.prevent="startAutoScroll('right')"
        @touchend="stopAutoScroll"
    >
      <svg class="svg-right">
        <use href="/img/sprite.svg#svg-right"></use>
      </svg>
    </button>

    <div class="table-wrapper scroll-x" id="tableScroll"
         ref="tableScroll" @scroll="updateScrollButtons">
      <table class="table editable-table has-special-drop" :style="positionsTableStyle">
        <colgroup v-if="!isImportMode">
          <col style="width: 45px" />
          <col style="width: 50px" />
          <col
              v-for="column in visibleTableColumns"
              :key="column.key"
              :style="{ width: `${getColumnWidth(column)}px` }"
          />
          <col style="width: 42px" />
        </colgroup>
        <thead class="table-thead">
        <tr>
          <th v-if="!isImportMode" class="cell-choose">
            <label class="checkbox-label">
              <input type="checkbox" name="chbx_all" :checked="isAllSelected()"
                     @change="toggleSelectAll">
              <span class="fake-checkbox"></span>
            </label>
          </th>
          <th v-if="!isImportMode" class="cell-option"></th>

          <template v-for="column in visibleTableColumns"
                    :key="column.key">
            <th
                :class="[column.class, { 'cell-identifier': isIdentifierColumn(column.key) }]"
            >
              <template v-if="column.sortKey">
                <button class="sort-btn" type="button" :title="getColumnLabel(column)" @click="setSort(column.sortKey)">
                  <span class="table-header-text">{{ getColumnLabel(column) }}</span>
                  <span class="sort-icons" aria-hidden="true">
            <span :class="['sort-up', { active: sortKey === column.sortKey && sortDir === 'asc' }]"></span>
            <span :class="['sort-down', { active: sortKey === column.sortKey && sortDir === 'desc' }]"></span>
          </span>
                </button>
              </template>
              <template v-else>
                <span class="table-header-text" :title="getColumnLabel(column)">{{ getColumnLabel(column) }}</span>
              </template>
            </th>
          </template>
          <th v-if="!isImportMode" class="cell-options"></th>
        </tr>
        </thead>
        <tbody class="table-tbody">
        <tr v-for="(position, index) in filteredPositions" :key="position.id" :class="{'error-row':position.error}">
          <td v-if="!isImportMode" class="cell-choose">
            <label class="checkbox-label">
              <input
                  type="checkbox"
                  :name="`chbx_row_${position.id}`"
                  :checked="isPositionSelected(position.id)"
                  @change="toggleRowSelection(position.id, $event)"
              />
              <span class="fake-checkbox"></span>
            </label>
          </td>
          <td v-if="!isImportMode" class="cell-option">
            <button class="btn-add has-tooltip has-tooltip-right" @click="addPositionBelow(index)">
              <div class="btn-tooltip">{{ t('actions.addRowBelow') }}</div>
            </button>
          </td>
          <td v-if="isColumnVisible('row_number')" class="cell-number">
            <span class="position-cell-text" :title="tooltipText(rowStart + index)">{{rowStart + index}}</span>
          </td>
          <td v-if="isColumnVisible('order_partner')" class="cell-order-code">
            <span class="position-cell-text" :title="tooltipText(position.partner)">{{position.partner}}</span>
          </td>
          <td v-if="isColumnVisible('article_old')" class="cell-articul cell-identifier">
            <input class="edit-inp" :title="tooltipText(position.article_old)" :readonly="!isManagerEdit" :class="position.color?'box-'+position.color:''" v-model="position.article_old" />
          </td>
          <td v-if="isColumnVisible('article_new')" class="cell-articul cell-identifier">
            <input class="edit-inp" :title="tooltipText(position.article_new)" v-model="position.article_new" />
          </td>
          <td v-if="isColumnVisible('order_code')" class="cell-order-code cell-identifier">
            <input class="edit-inp" :title="tooltipText(position.order_code)" :readonly="!isManagerEdit" v-model="position.order_code" />
          </td>
          <td v-if="isColumnVisible('order_number')" class="cell-order-code cell-identifier">
            <span class="position-cell-text position-cell-text--identifier" :title="tooltipText(position.order_number)">{{position.order_number}}</span>
          </td>
          <td v-if="isColumnVisible('order_date')" class="cell-order-code">
            <span class="position-cell-text" :title="tooltipText(position.order_date)">{{position.order_date}}</span>
          </td>
          <td v-if="isColumnVisible('supplier_number')" class="cell-order-code">
            <span class="position-cell-text" :title="tooltipText(position.supplier_number)">{{position.supplier_number}}</span>
          </td>
          <td v-if="isColumnVisible('order_invois')" class="cell-order-code">
            <span class="position-cell-text" :title="tooltipText(position.order_invois)">{{position.order_invois}}</span>
          </td>
          <td v-if="isColumnVisible('order_invois_date')" class="cell-order-code">
            <span class="position-cell-text" :title="tooltipText(position.order_invois_date)">{{position.order_invois_date}}</span>
          </td>

          <td v-if="isColumnVisible('photo') && !isImportMode" class="cell-camera cell-hide">
            <button
                class="btn-camera has-tooltip"
                :class="{'btn-camera--has-attachments': hasPositionAttachments(position)}"
                @click="showPhotoLoad = true; photoLoadId = position.id"
            >
              <svg class="svg-camera">
                <use href="/img/sprite.svg#svg-camera"></use>
              </svg>
              <svg v-if="hasPositionAttachments(position)" class="svg-camera-check">
                <use href="/img/sprite.svg#svg-check-fill"></use>
              </svg>
              <div class="btn-tooltip">{{ t('actions.addPhoto') }}</div>
            </button>
          </td>
          <td v-if="isColumnVisible('name')" class="cell-name">
            <div v-if="!position.name" class="label-error">
              <span>{{ t('errors.fieldNotFilled') }}</span>
            </div>
            <textarea
                :readonly="!isManagerEdit"
                :class="{'edit-textarea':isManagerEdit}"
                :title="tooltipText(position.name)"
                name="prod_name"
                rows="1"
                wrap="off"
                v-model="position.name"
            >{{position.name}}</textarea>
          </td>
          <td v-if="isColumnVisible('price')" class="cell-price">
            <div v-if="isManagerEdit" class="price-drop price-editable">
              <input class="edit-inp" :title="tooltipText(position.price)" v-model="position.price" />
              <div class="price-currency">
                {{position.currency}}
              </div>
            </div>
            <div v-else class="position-cell-text" :title="tooltipText(formatPrice(position))">
              {{position.price}} {{position.currency}}
            </div>
          </td>
          <td v-if="isColumnVisible('quantity_ordered')" class="cell-counter">
            <CounterInput :readonly="!isManagerEdit" v-model="position.quantity_ordered" :min="0" />
          </td>
          <td v-if="isColumnVisible('quantity_received')" class="cell-counter">
            <CounterInput :readonly="!isManagerEdit" v-model="position.quantity_received" :min="0" />
          </td>
          <td v-if="isColumnVisible('quantity_shipped')" class="cell-counter">
            <CounterInput v-model="position.quantity_shipped" :min="0" />
          </td>
          <td v-if="isColumnVisible('storage_shipment')" class="cell-storage">
            <SingleSelectDropdown
                v-model="position.storage_shipment"
                :options="storage_shipmentOptions[position.partner_id]"
                label=""
                :placeholder="t('form.select')"
                name="role"
            />
          </td>
          <td v-if="isColumnVisible('storage_delivery')" class="cell-storage">
            <SingleSelectDropdown
                :readonly="!isManagerEdit"
                v-model="position.storage_delivery_id"
                :options="storage_deliveryOptionsAll"
                label=""
                :placeholder="t('form.select')"
                name="role"
            />
          </td>
          <td v-if="isColumnVisible('contract_term')" class="cell-period">
            <input :readonly="!isManagerEdit" class="edit-inp" :title="tooltipText(position.contract_term)" v-model="position.contract_term" @input="recalculatePlannedArrival(position)"/>
          </td>

          <td v-if="isColumnVisible('date_supplier_order')" class="cell-calendar">
            <div class="calendar-field">
              <Datepicker
                  v-model="position.date_supplier_order"
                  class="calendar-input position-date-input"
                  :clearable="true"
                  :aria-labels="{ clearInput: t('dates.clearDate') }"
                  :title="tooltipText(formatDateForDisplay(position.date_supplier_order))"
                  placeholder=""
                  :enable-time-picker="false"
                  auto-apply
                  format="dd.MM.yyyy"
                  :locale="locale"
                  teleport="body"
                  @update:model-value="recalculatePlannedArrival(position)"
              />
              <span class="icon-calendar"></span>
            </div>
          </td>
          <td v-if="isColumnVisible('date_planned_arrival')" class="cell-calendar">
            <input
                class="edit-inp"
                :value="formatDateForDisplay(position.date_planned_arrival)"
                :title="tooltipText(formatDateForDisplay(position.date_planned_arrival))"
                readonly
            />
          </td>
          <td v-if="isColumnVisible('date_arrival_supplier')" class="cell-calendar">
            <div class="calendar-field">
              <Datepicker
                  v-model="position.date_arrival_supplier"
                  class="calendar-input position-date-input"
                  :clearable="true"
                  :aria-labels="{ clearInput: t('dates.clearDate') }"
                  :title="tooltipText(formatDateForDisplay(position.date_arrival_supplier))"
                  placeholder=""
                  :enable-time-picker="false"
                  auto-apply
                  format="dd.MM.yyyy"
                  :locale="locale"
                  teleport="body"
              />
              <span class="icon-calendar"></span>
            </div>
          </td>
          <td v-if="isColumnVisible('date_ready_shipment')" class="cell-calendar">
            <div class="calendar-field">
              <Datepicker
                  v-model="position.date_ready_shipment"
                  class="calendar-input position-date-input"
                  :clearable="true"
                  :aria-labels="{ clearInput: t('dates.clearDate') }"
                  :title="tooltipText(formatDateForDisplay(position.date_ready_shipment))"
                  placeholder=""
                  :enable-time-picker="false"
                  auto-apply
                  format="dd.MM.yyyy"
                  :locale="locale"
                  teleport="body"
              />
              <span class="icon-calendar"></span>
            </div>
          </td>
          <td v-if="isColumnVisible('date_order_shipment')" class="cell-calendar">
            <div class="calendar-field">
              <Datepicker
                  v-model="position.date_order_shipment"
                  class="calendar-input position-date-input"
                  :clearable="true"
                  :aria-labels="{ clearInput: t('dates.clearDate') }"
                  :title="tooltipText(formatDateForDisplay(position.date_order_shipment))"
                  placeholder=""
                  :enable-time-picker="false"
                  auto-apply
                  format="dd.MM.yyyy"
                  :locale="locale"
                  teleport="body"
              />
              <span class="icon-calendar"></span>
            </div>
          </td>
          <td v-if="isColumnVisible('comment')" class="cell-comment">
            <textarea class="edit-textarea" :title="tooltipText(position.comment)" rows="1" wrap="off" v-model="position.comment"></textarea>
          </td>
          <td v-if="isColumnVisible('status')" class="cell-status">
            <SingleSelectDropdown
                :readonly="!isManagerEdit"
                v-model="position.status"
                :options="translatedStatusOptions"
                label=""
                :placeholder="t('form.select')"
                name="role"
            />
          </td>
          <td v-if="isColumnVisible('marking')" class="cell-comment">
            <input v-if="isImportMode" class="edit-inp" v-model="position.marking_id" />
            <span v-else class="position-cell-text" :title="tooltipText(position.marking)">{{ position.marking }}</span>
          </td>
          <td v-if="!isImportMode" class="cell-options">
            <button class="btn-del has-tooltip" @click="showModalRemove = true; removePosition = position">
              <div class="btn-tooltip">{{ t('actions.delete') }}</div>
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div v-if="showPagination" class="positions-pagination flex-center">
    <button class="btn-outline" type="button" :disabled="!paginationState.has_prev" @click="changePage(paginationState.page - 1)">
      {{ t('pagination.prev') }}
    </button>
    <button
        v-for="page in visiblePaginationPages"
        :key="page"
        :class="page === paginationState.page ? 'btn' : 'btn-outline'"
        type="button"
        @click="changePage(page)"
    >
      {{ page }}
    </button>
    <button class="btn-outline" type="button" :disabled="!paginationState.has_next" @click="changePage(paginationState.page + 1)">
      {{ t('pagination.next') }}
    </button>
  </div>

  <div class="table-total">
    <div class="container flex">
      <div class="table-total-content flex">
        <div class="table-total-title font-20">{{ t('totals.total') }}</div>
        <div class="table-total-group flex">
          <div class="table-total-item flex">
            <p class="muted-14">{{ t('totals.quantity') }}</p>
            <p class="h2">{{ totalQuantity }}</p>
          </div>
          <div class="table-total-item flex" v-if="mode === 'order' || mode === 'import'">
            <p class="muted-14">{{ t(mode === 'order' ? 'totals.pageSum' : 'totals.sum') }}</p>
            <div class="price-drop">
              <span class="price-value">{{ formattedTotalSum }}</span>
              <div class="currency-value">{{order.currency}}</div>
            </div>
          </div>
          <div class="table-total-item flex" v-if="mode === 'order'">
            <p class="muted-14">{{ t('totals.orderTotalSum') }}</p>
            <div class="price-drop">
              <span class="price-value">{{ formattedOrderTotalSum }}</span>
              <div class="currency-value">{{order.currency}}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isImportMode" class="buttons-grid flex ml-auto">
        <button class="btn-outline w-100" :class="{loader:isParentSubmitting === 'draft'}" type="button" @click="$emit('save',false)">
          {{ t('orderPage.saveAsDraft') }}
        </button>
        <button class="btn w-100" :class="{loader:isParentSubmitting === 'send'}" type="button" @click="$emit('save',true)">
          {{ t('orderPage.sendPartner') }}
        </button>
      </div>
    </div>
  </div>

  <div v-if="!isImportMode" class="modal-frame" :class="{active:showModalRemove}">
    <div class="modal-frame-content">
      <button class="btn-close" rel="button_close" type="button" @click="showModalRemove = false; removePosition = null">
        <svg class="svg-cross">
          <use href="/img/sprite.svg#svg-cross"></use>
        </svg>
      </button>
      <div class="modal-frame-header">
        <div class="h3">{{ t('modals.confirmDeletion') }}</div>
      </div>
      <div v-if="removePosition" class="modal-footer font-18">
        <p class="txt-center">{{ t('modals.deleteRowWithArticle', { article: removePosition.article_old }) }}</p>
      </div>
      <div class="flex-center">
        <button class="btn" type="button" @click="removePosition && removePositionFromOrder(removePosition)">{{ t('modals.yes') }}</button>
        <button class="btn-outline" type="button" @click="showModalRemove = false; removePosition = null">{{ t('modals.no') }}</button>
      </div>
    </div>
  </div>

  <!-- Модальное окно массового редактирования -->
  <div v-if="!isImportMode" class="modal-frame modal-medium" :class="{active: showEdit}">
    <div class="modal-frame-content">
      <button class="btn-close" rel="button_close" type="button" @click="closeEditModal">
        <svg class="svg-cross">
          <use href="/img/sprite.svg#svg-cross"></use>
        </svg>
      </button>
      <div class="modal-frame-header flex-column">
        <div class="h3">{{ t('modals.edit') }}</div>
        <div class="label">{{ t('modals.selected') }}: {{ selectedPositions.length }} {{ pluralizePositions(selectedPositions.length) }}</div>
      </div>
      <div class="modal-frame-body">
        <form class="order-edit-form grid-gap-20" @submit.prevent="applyBulkEdit">
          <div class="form-item">
            <label class="form-label">{{ t('form.storageShipment') }}</label>
            <SingleSelectDropdown
                v-model="bulkEditData.storage_shipment"
                :options="isManagerEdit?bulkStorageShipmentOptions:storage_shipmentOptions[user.id]"
                label=""
                :placeholder="t('modals.noChange')"
                name="bulk_storage_shipment"
                :clearable="true"
            />
          </div>
          <div class="form-item" v-if="isManagerEdit">
            <label class="form-label">{{ t('form.storageDelivery') }}</label>
            <SingleSelectDropdown
                v-model="bulkEditData.storage_delivery_id"
                :options="storage_deliveryOptionsAll"
                label=""
                :placeholder="t('modals.noChange')"
                name="bulk_storage_delivery"
                :clearable="true"
            />
          </div>
          <div class="two-columns" v-if="isManagerEdit">
            <div class="form-item">
              <label class="form-label">{{ t('orderTable.quantity_ordered') }}</label>
              <input
                  v-model="bulkEditData.quantity_ordered"
                  class="edit-inp"
                  type="number"
                  min="0"
                  :placeholder="t('modals.noChange')"
              />
            </div>
            <div class="form-item">
              <label class="form-label">{{ t('orderTable.quantity_received') }}</label>
              <input
                  v-model="bulkEditData.quantity_received"
                  class="edit-inp"
                  type="number"
                  min="0"
                  :placeholder="t('modals.noChange')"
              />
            </div>
            <div class="form-item">
              <label class="form-label">{{ t('orderTable.quantity_shipped') }}</label>
              <input
                  v-model="bulkEditData.quantity_shipped"
                  class="edit-inp"
                  type="number"
                  min="0"
                  :placeholder="t('modals.noChange')"
              />
            </div>
          </div>
          <div class="two-columns calendar-group">
            <div class="form-item form-calendar" v-if="isManagerEdit">
              <label class="calendar-label">{{ t('filters.status') }}</label>
              <SingleSelectDropdown
                  v-model="bulkEditData.status"
                  :options="translatedStatusOptions"
                  label=""
                  :placeholder="t('modals.noChange')"
                  name="bulk_storage_delivery"
                  :clearable="true"
              />
            </div>
            <div class="form-item form-calendar">
              <label class="calendar-label">{{ t('dates.dateSupplierOrder') }}</label>
              <div class="calendar-field">
                <Datepicker
                    v-model="bulkEditData.date_supplier_order"
                    class="calendar-input"
                    :placeholder="t('modals.noChange')"
                    :enable-time-picker="false"
                    auto-apply
                    format="dd.MM.yyyy"
                    :locale="locale"
                    teleport="body"
                />
                <span class="icon-calendar"></span>
              </div>
            </div>
            <div class="form-item form-calendar">
              <label class="calendar-label">{{ t('dates.dateArrivalSupplier') }}</label>
              <div class="calendar-field">
                <Datepicker
                    v-model="bulkEditData.date_arrival_supplier"
                    class="calendar-input"
                    :placeholder="t('modals.noChange')"
                    :enable-time-picker="false"
                    auto-apply
                    format="dd.MM.yyyy"
                    :locale="locale"
                    teleport="body"
                />
                <span class="icon-calendar"></span>
              </div>
            </div>
            <div class="form-item form-calendar">
              <label class="calendar-label">{{ t('dates.dateReadyShipment') }}</label>
              <div class="calendar-field">
                <Datepicker
                    v-model="bulkEditData.date_ready_shipment"
                    class="calendar-input"
                    :placeholder="t('modals.noChange')"
                    :enable-time-picker="false"
                    auto-apply
                    format="dd.MM.yyyy"
                    :locale="locale"
                    teleport="body"
                />
                <span class="icon-calendar"></span>
              </div>
            </div>

            <div class="form-item form-calendar">
              <label class="calendar-label">{{ t('dates.dateOrderShipment') }}</label>
              <div class="calendar-field">
                <Datepicker
                    v-model="bulkEditData.date_order_shipment"
                    class="calendar-input"
                    :placeholder="t('modals.noChange')"
                    :enable-time-picker="false"
                    auto-apply
                    format="dd.MM.yyyy"
                    :locale="locale"
                    teleport="body"
                />
                <span class="icon-calendar"></span>
              </div>
            </div>
          </div>
          <div class="form-buttons flex-1">
            <button class="btn" type="submit" :disabled="!hasSelectedPositions">{{ t('modals.apply') }}</button>
            <button class="btn-outline" type="button" @click="closeEditModal">{{ t('modals.cancel') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <Attachment :isOpen="showAttachment" @close="showAttachment = false" :items="positions" />
  <History v-if="showHistory" :isOpen="showHistory" @close="showHistory = false" />
  <Settings
      :isOpen="showSettings"
      :items="settingsColumns"
      :selected="selectedColumnKeys"
      @close="showSettings = false"
      @apply="applyColumnSettings"
  />
  <PhotoLoad
      :isOpen="showPhotoLoad"
      :id="photoLoadId"
      @close="showPhotoLoad = false"
      @saved="handlePhotoSaved"
  />

</template>

<script>
import axios from 'axios';
import {mapActions, mapGetters} from "vuex";
import Attachment from '@/components/order/Attachment.vue';
import History from '@/components/order/History.vue';
import Edit from '@/components/order/Edit.vue';
import Settings from '@/components/order/Settings.vue';
import { useDebounceFn } from '@vueuse/core';

import { useI18n } from 'vue-i18n';

import { sortArray, getSortValue } from '@/utils/sortUtils';
import { translateApiError } from '@/utils/apiErrorUtils';
import { isOrderStatus, POSITION_STATUS_VALUES } from '@/utils/statusUtils';
import { localizedDisplayValue, localizedEntityValue } from '@/utils/transliteration';
import { sortOptionsByLabel } from '@/utils/optionSortUtils';
import scrollManagerMixin from '@/mixin/scrollManagerMixin';
import CheckboxFilter from "@/components/filters/CheckboxFilter.vue";
import MultiSelectFilter from "@/components/filters/MultiSelectFilter.vue";
import FieldSearchFilter from "@/components/filters/FieldSearchFilter.vue";
import Datepicker from "@vuepic/vue-datepicker";
import SingleSelectDropdown from "@/components/form/SingleSelectDropdown.vue";
import CounterInput from '@/components/form/CounterInput.vue';
import PhotoLoad from "@/components/order/PhotoLoad.vue";
import { readFiltersExpanded, writeFiltersExpanded } from '@/utils/filterExpansion';

const IDENTIFIER_COLUMN_KEYS = new Set(['article_old', 'article_new', 'order_code', 'order_number']);

const POSITION_COLUMN_WIDTHS = Object.freeze({
  row_number: 85,
  order_partner: 160,
  article_old: 174,
  article_new: 174,
  order_code: 160,
  order_number: 180,
  order_date: 160,
  supplier_number: 160,
  order_invois: 160,
  order_invois_date: 160,
  photo: 70,
  name: 355,
  price: 160,
  quantity_ordered: 150,
  quantity_received: 150,
  quantity_shipped: 150,
  storage_shipment: 240,
  storage_delivery: 240,
  contract_term: 140,
  date_supplier_order: 180,
  date_planned_arrival: 160,
  date_arrival_supplier: 180,
  date_ready_shipment: 180,
  date_order_shipment: 180,
  comment: 320,
  status: 170,
  marking: 320,
});


export default {
  components: {
    PhotoLoad,
    SingleSelectDropdown,
    Datepicker, FieldSearchFilter, MultiSelectFilter, CheckboxFilter,
    Attachment, History, Edit, Settings, CounterInput
  },
  mixins: [scrollManagerMixin],
  emits: ['update:positions', 'change-page', 'change-sort', 'change-filters', 'show-message-history'],
  props: {
    order: {
      type: Object,
      default: () => {},
    },
    positions: {
      type: [Array, Object],
      default: () => [],
    },
    pagination: {
      type: Object,
      default: null,
    },
    serverSort: {
      type: Object,
      default: null,
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    initialFilters: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    mode: '',
    orderLoaded: false,
    isParentSubmitting: false
  },
  data: () => ({
    showAttachment: false,
    showHistory: false,
    showEdit: false,
    showSettings: false,
    showDates: false,
    removePosition: null,
    showModalRemove: false,
    sortKey: '',
    sortDir: 'asc',

    filtersState: {
      selectedStorage_delivery: [],
      selectedEmpty_date: [],
      selectedFilled_date: [],
      showArrived: false,
      invoiceOrderSearch: '',
      selectedFilial: [],
      selectedPartner: [],
      selectedColor: [],
      selectedMarking: [],
    },
    originalPositions: null,
    empty_dateOptions: [
      { label: 'dates.dateSupplierOrder', value: 'date_supplier_order' },
      { label: 'dates.datePlannedArrival', value: 'date_planned_arrival' },
      { label: 'dates.dateArrivalSupplier', value: 'date_arrival_supplier' },
      { label: 'dates.dateReadyShipment', value: 'date_ready_shipment' },
      { label: 'dates.dateOrderShipment', value: 'date_order_shipment' },
    ],
    statusOptions: [
      { label: 'status.inProgress', value: POSITION_STATUS_VALUES.inProgress },
      { label: 'status.arrived', value: POSITION_STATUS_VALUES.arrived },
      { label: 'status.completed', value: POSITION_STATUS_VALUES.completed },
      { label: 'status.refusal', value: POSITION_STATUS_VALUES.refusal },
    ],
    showPhotoLoad: false,
    photoLoadId: '',

    selectedPositions: [],
    bulkEditData: {
      storage_shipment: null,
      storage_delivery_id: null,
      quantity_ordered: null,
      quantity_received: null,
      quantity_shipped: null,
      date_supplier_order: null,
      date_arrival_supplier: null,
      date_ready_shipment: null,
      date_order_shipment: null,
      status: null,
    },
    isApplyingServerUpdate: false,
    filtersExpanded: false,

  }),
  setup() {
    const { t, locale } = useI18n();
    const debouncedSave = useDebounceFn((changes, saveMethod) => {
      saveMethod(changes);
      console.log('Сохранение изменений...', changes);
    }, 2000);
    const debouncedFilterChange = useDebounceFn((emitFilters) => {
      emitFilters();
    }, 500);

    return { t, locale, debouncedSave, debouncedFilterChange };
  },
  created() {
    this.restoreFiltersExpanded();
    this.syncFiltersFromInitial();
    this.syncSortFromServer();
    this.originalPositions = JSON.parse(JSON.stringify(this.positions));
  },
  mounted() {

  },
  beforeUnmount() {

  },
  watch: {
    orderLoaded(val) {
      if (val) {
        this.updateScrollOnDataChange();
      }
    },
    filteredPositions() {
      this.updateScrollOnDataChange();
      this.$nextTick(() => {
        // лёгкая проверка состояния sticky без тяжёлой пересинхронизации
        this.onWindowScrollSticky();
      });
    },
    positions: {
      handler(newPositions, oldPositions) {
        if (!this.originalPositions) {
          return;
        }
        if (this.isApplyingServerUpdate) {
          return;
        }
        if (this.isImportMode) {
          this.$emit('update:positions', newPositions);
          return;
        }
        if (this.usesBackendSort && newPositions !== oldPositions) {
          this.originalPositions = JSON.parse(JSON.stringify(newPositions || []));
          this.selectedPositions = [];
          return;
        }
        if (this.isPositionsPageReplaced(newPositions)) {
          this.originalPositions = JSON.parse(JSON.stringify(newPositions));
          this.selectedPositions = [];
          return;
        }
        const changes = this.getChangedPositions(newPositions);

        if (changes.length > 0) {
          console.log('Обнаружены изменения:', changes);
          this.showSaving(this.t('notifications.saving'));
          this.debouncedSave(changes, this.saveChangesToServer);
        }
      },
      deep: true
    },
    serverSort: {
      handler() {
        this.syncSortFromServer();
      },
      deep: true
    },
    initialFilters: {
      handler() {
        this.syncFiltersFromInitial();
      },
      deep: true
    },
    'filtersState.selectedFilial': {
      handler() {
        this.emitFiltersChange();
      },
      deep: true
    },
    'filtersState.selectedPartner': {
      handler() {
        this.emitFiltersChange();
      },
      deep: true
    },
    'filtersState.selectedColor': {
      handler() {
        this.emitFiltersChange();
      },
      deep: true
    },
    'filtersState.selectedStorage_delivery': {
      handler() {
        this.emitFiltersChange();
      },
      deep: true
    },
    'filtersState.selectedMarking': {
      handler() {
        this.emitFiltersChange();
      },
      deep: true
    },
    'filtersState.selectedEmpty_date': {
      handler() {
        this.emitFiltersChange();
      },
      deep: true
    },
    'filtersState.selectedFilled_date': {
      handler() {
        this.emitFiltersChange();
      },
      deep: true
    },
    'filtersState.showArrived'() {
      this.emitFiltersChange();
    },
    'filtersState.invoiceOrderSearch'() {
      if (this.isApplyingServerUpdate) return;
      this.debouncedFilterChange(() => this.emitFiltersChange());
    },
    identifierColumnWidths: {
      handler() {
        this.updateScrollOnDataChange();
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters('positions', [
      'storage_shipment',
      'storage_delivery'
    ]),
    ...mapGetters([
      'user'
    ]),
    ...mapGetters('app', [
      'columnSettings'
    ]),
    isNew() {
      return (this.user.role === 'manager' || this.user.role === 'admin') && isOrderStatus(this.order.status, 'new');
    },
    isParnterEdit() {
      return this.user.role === 'supplier';
    },
    isManagerEdit() {
      return this.isImportMode || this.user.role === 'manager' || this.user.role === 'admin';
    },
    isImportMode() {
      return this.mode === 'import';
    },
    filtersStorageKey() {
      return `sumiteclk:filters-expanded:${this.mode === 'all' ? 'position' : 'order'}`;
    },
    filtersContainerId() {
      return `positions-filters-${this.mode === 'all' ? 'position' : 'order'}`;
    },
    storage_deliveryOptionsAll() {
      let storage_delivery = [];
      for (const [key, value] of Object.entries(this.storage_delivery)) {
        storage_delivery.push({
          label: this.storageLabel(value),
          value: key
        });
      }
      return storage_delivery;
    },
    storage_shipmentOptions() {
      let storage_shipment = {};
      for (const [key, value] of Object.entries(this.storage_shipment)) {
        storage_shipment[key] = value.map(item => ({
          label: item,
          value: item
        }));
      }
      return storage_shipment;
    },
    filtersConfig() {
      return [
        { key: 'partner', type: 'multi-select', labelKey: 'filters.partner', stateKey: 'selectedPartner', options: this.partnerOptions, mode: 'all' },
        { key: 'filial', type: 'multi-select', labelKey: 'filters.filial', stateKey: 'selectedFilial', options: this.filialOptions, mode: 'all' },
        { key: 'color', type: 'multi-select', labelKey: 'filters.color', stateKey: 'selectedColor', options: this.colorOptions, mode: 'all' },
        { key: 'storage_delivery', type: 'multi-select', labelKey: 'filters.storage_delivery', stateKey: 'selectedStorage_delivery', options: this.storage_deliveryOptions, mode: 'order' },
        { key: 'marking', type: 'multi-select', labelKey: 'filters.marking', stateKey: 'selectedMarking', options: this.markingOptions, mode: 'order' },
        { key: 'empty_date', type: 'multi-select', labelKey: 'filters.empty_date', stateKey: 'selectedEmpty_date', options: this.translatedEmptyDateOptions, mode: 'order' },
        { key: 'filled_date', type: 'multi-select', labelKey: 'filters.filled_date', stateKey: 'selectedFilled_date', options: this.translatedEmptyDateOptions, mode: 'order' },
        { key: 'search', type: 'search', labelKey: 'filters.search', placeholderKey: this.positionSearchPlaceholderKey, stateKey: 'invoiceOrderSearch', fields: ['article_old', 'article_new', 'order_number', 'order_invois', 'supplier_number'], mode: 'order' },
        { key: 'arrived', type: 'checkbox', labelKey: 'filters.arrived', stateKey: 'showArrived', mode: 'order' },
      ];
    },
    identifierColumnWidths() {
      const rows = Array.isArray(this.positions) ? this.positions : [];

      return Array.from(IDENTIFIER_COLUMN_KEYS).reduce((widths, key) => {
        const column = this.tableColumns.find(item => item.key === key);
        const headerWidth = this.measureTableText(column ? this.getColumnLabel(column) : '') + 48;
        const valueWidth = rows.reduce((maxWidth, position) => {
          return Math.max(maxWidth, this.measureTableText(position?.[key]));
        }, 0);
        const contentPadding = key === 'order_number' ? 42 : 82;

        widths[key] = Math.max(
            POSITION_COLUMN_WIDTHS[key],
            Math.ceil(headerWidth),
            Math.ceil(valueWidth + contentPadding)
        );
        return widths;
      }, {});
    },
    positionsTableWidth() {
      if (this.isImportMode) {
        return null;
      }

      const serviceColumnsWidth = 45 + 50 + 42;
      return serviceColumnsWidth + this.visibleTableColumns.reduce((width, column) => {
        return width + this.getColumnWidth(column);
      }, 0);
    },
    positionsTableStyle() {
      if (this.isImportMode) {
        return undefined;
      }

      return {
        width: `${this.positionsTableWidth}px`,
        minWidth: '100%',
      };
    },
    tableColumns() {
      return [
        { key: 'row_number', class: 'cell-number', sortKey: 'row_number', labelKey: 'orderTable.row_number' },
        { key: 'order_partner', class: 'cell-order-code', sortKey: 'partner', labelKey: 'orderPage.supplier', mode: 'all' },
        { key: 'article_old', class: 'cell-articul articul-old', sortKey: 'article_old', labelKey: 'orderTable.article_old' },
        { key: 'article_new', class: 'cell-articul articul-new', sortKey: 'article', labelKey: 'orderTable.article_new' },
        { key: 'order_code', class: 'cell-order-code', sortKey: 'order_code', labelKey: 'orderTable.order_code' },
        { key: 'order_number', class: 'cell-order-code', sortKey: 'order_number', labelKey: 'orderTable.order_number', mode: 'all' },
        { key: 'order_date', class: 'cell-order-code', sortKey: 'order_date', labelKey: 'orderTable.order_date', mode: 'all' },
        { key: 'supplier_number', class: 'cell-order-code', sortKey: 'supplier_number', labelKey: 'orderTable.supplier_number', mode: 'all' },
        { key: 'order_invois', class: 'cell-order-code', sortKey: 'order_invois', labelKey: 'orderTable.order_invois', mode: 'all' },
        { key: 'order_invois_date', class: 'cell-order-code', sortKey: 'order_invois_date', labelKey: 'orderTable.order_invois_date', mode: 'all' },
        { key: 'photo', class: 'cell-camera cell-hide' },
        { key: 'name', class: 'cell-name', sortKey: 'name', labelKey: 'orderTable.name' },
        { key: 'price', class: 'cell-price', sortKey: 'price', labelKey: 'orderTable.price' },
        { key: 'quantity_ordered', class: 'cell-counter', sortKey: 'quantity_ordered', labelKey: 'orderTable.quantity_ordered' },
        { key: 'quantity_received', class: 'cell-counter', sortKey: 'quantity_received', labelKey: 'orderTable.quantity_received' },
        { key: 'quantity_shipped', class: 'cell-counter', sortKey: 'quantity_shipped', labelKey: 'orderTable.quantity_shipped' },
        { key: 'storage_shipment', class: 'cell-storage', sortKey: 'storage_shipment', labelKey: 'orderTable.storage_shipment' },
        { key: 'storage_delivery', class: 'cell-storage', sortKey: 'storage_delivery', labelKey: 'orderTable.storage_delivery' },
        { key: 'contract_term', class: 'cell-period', sortKey: 'contract_term', labelKey: this.isImportMode ? 'orderImport.deadline' : 'orderTable.contract_term' },

        { key: 'date_supplier_order', class: 'cell-calendar', sortKey: 'date_supplier_order', labelKey: 'orderTable.date_supplier_order' },
        { key: 'date_planned_arrival', class: 'cell-calendar', sortKey: 'date_planned_arrival', labelKey: 'orderTable.date_planned_arrival' },
        { key: 'date_arrival_supplier', class: 'cell-calendar', sortKey: 'date_arrival_supplier', labelKey: 'orderTable.date_arrival_supplier' },
        { key: 'date_ready_shipment', class: 'cell-calendar', sortKey: 'date_ready_shipment', labelKey: 'orderTable.date_ready_shipment' },

        { key: 'date_order_shipment', class: 'cell-calendar', sortKey: 'date_order_shipment', labelKey: 'orderTable.date_order_shipment' },
        { key: 'comment', class: 'cell-comment' },
        { key: 'status', class: 'cell-status', labelKey: 'filters.status' },
        { key: 'marking', class: 'cell-comment', sortKey: 'marking', labelKey: 'orderTable.marking' },
      ];
    },
    availableTableColumns() {
      return this.tableColumns.filter(column => !column.mode || column.mode === this.mode || (this.isImportMode && column.mode === 'order'));
    },
    visibleTableColumns() {
      if (this.isImportMode) {
        return this.availableTableColumns.filter(column => ![
          'photo',
          'xml_id',
          /*'article_new',
          'quantity_received',
          'quantity_shipped',
          'storage_shipment',
          'date_order_shipment',
          'date_supplier_order',
          'date_arrival_supplier',
          'date_ready_shipment',
          'date_planned_arrival',
          'comment',
          'status',*/
        ].includes(column.key));
      }
      return this.availableTableColumns.filter(column => this.isColumnSelected(column.key));
    },
    settingsColumns() {
      return this.availableTableColumns.map(column => ({
        key: column.key,
        label: this.getColumnLabel(column),
      }));
    },
    selectedColumnKeys() {
      return this.availableTableColumns
          .filter(column => this.isColumnSelected(column.key))
          .map(column => column.key);
    },
    translatedEmptyDateOptions() {
      return this.empty_dateOptions.map(option => ({ ...option, label: this.t(option.label) }));
    },
    translatedStatusOptions() {
      return this.statusOptions.map(option => ({ ...option, label: this.t(option.label) }));
    },
    paginationState() {
      return {
        page: Number(this.pagination?.page) || 1,
        page_size: Number(this.pagination?.page_size) || 100,
        total: Number(this.pagination?.total) || 0,
        pages: Number(this.pagination?.pages) || 1,
        offset: Number(this.pagination?.offset) || 0,
        has_prev: Boolean(this.pagination?.has_prev),
        has_next: Boolean(this.pagination?.has_next),
      };
    },
    showPagination() {
      return !this.isImportMode && this.paginationState.pages > 1;
    },
    rowStart() {
      return this.paginationState.offset + 1;
    },
    visiblePaginationPages() {
      const current = this.paginationState.page;
      const total = this.paginationState.pages;
      const from = Math.max(1, current - 2);
      const to = Math.min(total, current + 2);
      const pages = [];

      for (let page = from; page <= to; page += 1) {
        pages.push(page);
      }

      return pages;
    },
    sortedPositions() {
      if (this.usesBackendSort) {
        return this.positions || [];
      }

      return sortArray(this.positions || [], this.sortKey, this.sortDir);
    },
    usesBackendSort() {
      return !this.isImportMode && Boolean(this.pagination);
    },
    positionSearchPlaceholderKey() {
      return this.mode === 'all'
          ? 'filters.searchExpandedPositions'
          : 'filters.searchOrderPositions';
    },
    filteredPositions() {
      if (this.usesBackendSort) {
        return this.sortedPositions;
      }

      const search = this.normalizeSearch(this.filtersState.invoiceOrderSearch);

      return this.sortedPositions.filter(position => {
        const matchPartner = !this.filtersState.selectedPartner.length || this.filtersState.selectedPartner.includes(position.partner);
        const matchFilial = !this.filtersState.selectedFilial.length || this.filtersState.selectedFilial.includes(position.filial);
        const matchColor = !this.filtersState.selectedColor.length || this.filtersState.selectedColor.includes(position.color);
        const matchStorageDelivery = !this.filtersState.selectedStorage_delivery.length || this.filtersState.selectedStorage_delivery.includes(position.storage_delivery);
        const matchMarking = !this.filtersState.selectedMarking.length || this.filtersState.selectedMarking.includes(position.marking);

        const matchArrived = !this.filtersState.showArrived || Boolean(!position?.arrived);

        const matchArticleSearch =
            !search ||
            ['article_old', 'article_new', 'order_number', 'order_invois', 'supplier_number']
                .some(field => this.normalizeSearch(position?.[field]).includes(search));

        const matchEmptyDate = !this.filtersState.selectedEmpty_date.length ||
            this.filtersState.selectedEmpty_date.some(dateField => !this.isDateFilled(position?.[dateField]));
        const matchFilledDate = !this.filtersState.selectedFilled_date.length ||
            this.filtersState.selectedFilled_date.some(dateField => this.isDateFilled(position?.[dateField]));

        return matchPartner && matchFilial && matchColor &&
            matchStorageDelivery && matchMarking && matchArrived && matchArticleSearch &&
            matchEmptyDate && matchFilledDate;
      });
    },
    storage_deliveryOptions() {
      return this.getFilterOptions(
          'storage_delivery',
          position => position?.storage_delivery,
          (value, option) => this.displayEntityText(value, option?.label_en || option?.storage_delivery_en)
      );
    },
    markingOptions() {
      return this.getFilterOptions('marking', position => position?.marking);
    },
    filialOptions() {
      return this.getFilterOptions(
          'filial',
          position => position?.filial,
          (value, option) => this.displayEntityText(value, option?.label_en || option?.filial_en)
      );
    },
    partnerOptions() {
      const options = this.getFilterOptions(
          'partner',
          position => position?.partner,
          (value, option) => this.displayEntityText(value, option?.label_en || option?.partner_en)
      );

      return sortOptionsByLabel(options, this.displayLocale());
    },
    colorOptions() {
      return this.getFilterOptions('color', position => position?.color);
    },
    totalQuantity() {
      if (!this.isImportMode && this.pagination) {
        return this.paginationState.total;
      }

      return this.filteredPositions.reduce((sum, position) => {
        const quantity = parseFloat(position.quantity_ordered) || 0;
        return sum + quantity;
      }, 0);
    },
    totalSum() {
      return this.filteredPositions.reduce((sum, position) => {
        const price = parseFloat(position.price) || 0;
        const quantity = parseFloat(position.quantity_ordered) || 0;
        return sum + (price * quantity);
      }, 0);
    },
    formattedTotalSum() {
      return new Intl.NumberFormat('ru-RU').format(this.totalSum);
    },
    formattedOrderTotalSum() {
      const orderTotalSum = Number(this.order?.summa);
      return new Intl.NumberFormat('ru-RU').format(Number.isFinite(orderTotalSum) ? orderTotalSum : 0);
    },
    hasSelectedPositions() {
      return this.selectedPositions.length > 0;
    },
    bulkStorageShipmentOptions() {
      const allOptions = new Set();
      Object.values(this.storage_shipmentOptions).forEach(options => {
        options.forEach(option => {
          allOptions.add(JSON.stringify(option));
        });
      });
      return Array.from(allOptions).map(str => JSON.parse(str));
    },

  },
  methods: {
    ...mapActions('positions', [
      'addPosition'
    ]),
    ...mapActions('notify', [
      'addNotify',
      'showSaving',
      'hideSaving',
    ]),
    restoreFiltersExpanded() {
      this.filtersExpanded = this.isImportMode ? false : readFiltersExpanded(this.filtersStorageKey);
    },
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
      writeFiltersExpanded(this.filtersStorageKey, this.filtersExpanded);
      this.$nextTick(() => this.updateScrollOnDataChange());
    },
    setSort(key) {
      const nextDir = this.sortKey === key && this.sortDir === 'asc' ? 'desc' : 'asc';

      if (this.usesBackendSort) {
        this.$emit('change-sort', {
          key,
          dir: nextDir,
        });
        return;
      }

      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        return;
      }
      this.sortKey = key;
      this.sortDir = 'asc';
    },
    syncSortFromServer() {
      if (!this.serverSort) {
        return;
      }

      this.sortKey = this.serverSort.key || '';
      this.sortDir = this.serverSort.dir === 'desc' ? 'desc' : 'asc';
    },
    getColumnLabel(column) {
      if (column.labelKey) {
        return this.t(column.labelKey);
      }
      return this.t(`orderTable.${column.key}`);
    },
    tooltipText(value) {
      return value === null || value === undefined ? '' : String(value);
    },
    formatPrice(position) {
      return [position?.price, position?.currency]
          .filter(value => value !== null && value !== undefined && value !== '')
          .join(' ');
    },
    isIdentifierColumn(key) {
      return IDENTIFIER_COLUMN_KEYS.has(key);
    },
    getColumnWidth(column) {
      if (this.isIdentifierColumn(column.key)) {
        return this.identifierColumnWidths[column.key];
      }

      return POSITION_COLUMN_WIDTHS[column.key] || 160;
    },
    measureTableText(value) {
      const text = this.tooltipText(value);
      if (!text || typeof document === 'undefined') {
        return text.length * 8;
      }

      if (!this._tableTextMeasureContext) {
        const canvas = document.createElement('canvas');
        this._tableTextMeasureContext = canvas.getContext('2d');
      }

      if (!this._tableTextMeasureContext) {
        return text.length * 8;
      }

      this._tableTextMeasureContext.font = '15px Roboto, sans-serif';
      return this._tableTextMeasureContext.measureText(text).width;
    },
    normalizeColumnSettings(settings) {
      if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return {};
      }

      const normalized = {};
      Object.entries(settings).forEach(([mode, columns]) => {
        if (!columns || typeof columns !== 'object' || Array.isArray(columns)) {
          return;
        }

        normalized[mode] = {};
        Object.entries(columns).forEach(([key, value]) => {
          normalized[mode][key] = Boolean(value);
        });
      });
      return normalized;
    },
    isColumnSelected(key) {
      if (this.isImportMode) {
        return true;
      }
      const settings = this.normalizeColumnSettings(this.columnSettings);
      const modeSettings = settings[this.mode];

      if (!modeSettings || !Object.prototype.hasOwnProperty.call(modeSettings, key)) {
        return true;
      }

      return modeSettings[key];
    },
    isColumnVisible(key) {
      return this.availableTableColumns.some(column => column.key === key) && this.isColumnSelected(key);
    },
    hasPositionAttachments(position) {
      return Array.isArray(position?.photo) && position.photo.length > 0;
    },
    handlePhotoSaved() {
      this.isApplyingServerUpdate = true;
      this.showPhotoLoad = false;
      this.$nextTick(() => {
        this.originalPositions = JSON.parse(JSON.stringify(this.positions || []));
        this.isApplyingServerUpdate = false;
      });
    },
    async applyColumnSettings(selectedKeys) {
      const selectedSet = new Set(selectedKeys);
      const currentSettings = this.normalizeColumnSettings(this.columnSettings);

      currentSettings[this.mode] = {
        ...(currentSettings[this.mode] || {}),
      };

      this.availableTableColumns.forEach(column => {
        currentSettings[this.mode][column.key] = selectedSet.has(column.key);
      });

      try {
        await axios.post('/api/app/settings/', { column_settings: currentSettings }, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        await this.$store.dispatch('app/fetchApp');
        this.showSettings = false;
        this.$nextTick(() => this.updateScrollOnDataChange());
      } catch (error) {
        console.error('Ошибка при сохранении настроек колонок:', error);
        this.addNotify({text: this.t('notifications.columnSettingsError'), error: true});
      }
    },
    normalizeSearch(value) {
      return String(value ?? '').trim().toLowerCase();
    },
    buildUniqueOptions(source, getter, labelGetter = value => String(value)) {
      const unique = new Map();
      (source || []).forEach(item => {
        const value = getter(item);
        if (value === null || value === undefined || value === '') return;

        if (!unique.has(value)) {
          unique.set(value, {
            value,
            label: labelGetter(value, item),
          });
        }
      });
      return Array.from(unique.values());
    },
    getFilterOptions(key, fallbackGetter, labelGetter = value => String(value)) {
      const options = this.filterOptions?.[key];
      if (Array.isArray(options) && options.length) {
        return options.map(option => ({
          ...option,
          label: labelGetter(option.label ?? option.value, option),
        }));
      }

      return this.buildUniqueOptions(this.positions, fallbackGetter, labelGetter);
    },
    displayText(value) {
      return localizedDisplayValue(value, this.displayLocale());
    },
    displayEntityText(value, valueEn) {
      return localizedEntityValue(value, valueEn, this.displayLocale());
    },
    displayLocale() {
      return this.isSupplier() ? 'en' : this.locale;
    },
    storageLabel(value) {
      if (value && typeof value === 'object') {
        return this.displayEntityText(value.name, value.name_en);
      }

      return this.displayText(value);
    },
    isSupplier() {
      return this.user?.role === 'supplier';
    },
    syncFiltersFromInitial() {
      this.isApplyingServerUpdate = true;
      this.filtersState = {
        selectedStorage_delivery: this.normalizeFilterArray(this.initialFilters.storage_delivery),
        selectedEmpty_date: this.normalizeFilterArray(this.initialFilters.empty_date),
        selectedFilled_date: this.normalizeFilterArray(this.initialFilters.filled_date),
        showArrived: Boolean(this.initialFilters.arrived),
        invoiceOrderSearch: String(this.initialFilters.search || ''),
        selectedFilial: this.normalizeFilterArray(this.initialFilters.filial),
        selectedPartner: this.normalizeFilterArray(this.initialFilters.partner),
        selectedColor: this.normalizeFilterArray(this.initialFilters.color),
        selectedMarking: this.normalizeFilterArray(this.initialFilters.marking),
      };
      this.$nextTick(() => {
        this.isApplyingServerUpdate = false;
      });
    },
    normalizeFilterArray(value) {
      return Array.isArray(value) ? value.filter(item => item !== '') : [];
    },
    isDateFilled(value) {
      return value !== null && value !== undefined && value !== false && String(value).trim() !== '';
    },
    emitFiltersChange() {
      if (this.isImportMode || this.isApplyingServerUpdate) {
        return;
      }

      const filters = this.serializeFilters();
      // A delayed search callback must not turn a route/server sync into a new filter edit.
      const unchanged = Object.keys(filters).every(key => {
        if (Array.isArray(filters[key])) return JSON.stringify(filters[key]) === JSON.stringify(this.normalizeFilterArray(this.initialFilters[key]));
        if (key === 'arrived') return Boolean(filters[key]) === Boolean(this.initialFilters[key]);
        return filters[key] === String(this.initialFilters[key] || '').trim();
      });
      if (!unchanged) this.$emit('change-filters', filters);
    },
    serializeFilters() {
      return {
        filial: this.filtersState.selectedFilial,
        partner: this.filtersState.selectedPartner,
        color: this.filtersState.selectedColor,
        storage_delivery: this.filtersState.selectedStorage_delivery,
        marking: this.filtersState.selectedMarking,
        empty_date: this.filtersState.selectedEmpty_date,
        filled_date: this.filtersState.selectedFilled_date,
        arrived: this.filtersState.showArrived ? '1' : '',
        search: String(this.filtersState.invoiceOrderSearch || '').trim(),
      };
    },
    isPositionsPageReplaced(newPositions) {
      if (!Array.isArray(newPositions) || !Array.isArray(this.originalPositions)) {
        return false;
      }

      const originalIds = new Set(this.originalPositions.map(position => String(position.id)));
      const hasSamePosition = newPositions.some(position => originalIds.has(String(position.id)));

      return !hasSamePosition && (newPositions.length > 0 || this.originalPositions.length > 0);
    },

    changePage(page) {
      const nextPage = Math.min(Math.max(Number(page) || 1, 1), this.paginationState.pages);
      if (nextPage === this.paginationState.page) {
        return;
      }

      this.$emit('change-page', nextPage);
      this.scrollPageToTop();
    },

    scrollPageToTop() {
      if (typeof window === 'undefined') {
        return;
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },

    getChangedPositions(newPositions) {
      const changes = [];
      newPositions.forEach((position, index) => {
        const original = this.originalPositions[index];
        if (!original || JSON.stringify(position) !== JSON.stringify(original)) {
          changes.push({
            id: position.id,
            ...position
          });
        }
      });
      return changes;
    },

    formatDateForBackend(date) {
      if (!date) return null;

      if (typeof date === 'string') {
        const parts = this.parseBackendDateString(date);
        if (!parts) {
          return null;
        }
        return `${String(parts.day).padStart(2, '0')}.${String(parts.month).padStart(2, '0')}.${parts.year}`;
      }

      if (date instanceof Date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      }

      return null;
    },

    parseBackendDateString(date) {
      const value = String(date || '').trim();
      if (!value) {
        return null;
      }

      let match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (match) {
        return {
          day: Number(match[3]),
          month: Number(match[2]),
          year: Number(match[1]),
        };
      }

      match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (!match) {
        return null;
      }

      const first = Number(match[1]);
      const second = Number(match[2]);
      const year = Number(match[3]);

      if (first > 12) {
        return { day: first, month: second, year };
      }
      if (second > 12) {
        return { day: second, month: first, year };
      }

      return { day: second, month: first, year };
    },

    parsePositionDate(date) {
      if (!date) {
        return null;
      }
      if (date instanceof Date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      }
      if (typeof date === 'string') {
        const value = date.trim();
        let match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (match) {
          const first = Number(match[1]);
          const second = Number(match[2]);
          const day = second > 12 ? second : first;
          const month = second > 12 ? first : second;
          return new Date(Number(match[3]), month - 1, day);
        }
        match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
        if (match) {
          return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        }
      }
      const parsed = new Date(date);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },

    formatDateForDisplay(date) {
      const parsed = this.parsePositionDate(date);
      if (!parsed) {
        return '';
      }
      const day = String(parsed.getDate()).padStart(2, '0');
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}.${parsed.getFullYear()}`;
    },

    calculatePlannedArrival(datePlaced, contractTerm) {
      const date = this.parsePositionDate(datePlaced);
      if (!date || contractTerm === null || contractTerm === undefined || String(contractTerm).trim() === '') {
        return '';
      }
      const term = String(contractTerm).trim();
      if (/^-?\d+$/.test(term)) {
        date.setDate(date.getDate() + Number(term));
      }
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}.${date.getFullYear()}`;
    },

    recalculatePlannedArrival(position) {
      const plannedDate = this.calculatePlannedArrival(position.date_supplier_order, position.contract_term);
      if (plannedDate) {
        position.date_planned_arrival = plannedDate;
      }
    },

    preparePositionsForServer(positions) {
      return positions.map(position => {
        const prepared = { ...position };
        delete prepared.date_planned_arrival;
        const dateFields = [
          'date_order_shipment',
          'date_supplier_order',
          'date_arrival_supplier',
          'date_ready_shipment'
        ];
        dateFields.forEach(field => {
          if (Object.prototype.hasOwnProperty.call(prepared, field)) {
            // Axios omits null in multipart requests; an empty string explicitly clears the date.
            prepared[field] = prepared[field] ? this.formatDateForBackend(prepared[field]) : '';
          }
        });
        return prepared;
      });
    },

    async saveChangesToServer(changes) {
      try {
        const preparedChanges = this.preparePositionsForServer(changes);

        const response = await axios.post('/api/order/positions/', { positions: preparedChanges },
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
        if (response.data.status == 'error') {
          let serverError = '';
          for (let i in response.data.errors) {
            console.log(typeof response.data.errors[i].code)
            if (typeof response.data.errors[i].code == 'number') {
              serverError = this.t('errors.serverError');
              break;
            }
            else
              serverError += translateApiError(response.data.errors[i], this.t) + "<br>";
          }

          if (response.data.data.errors) {
            response.data.data.errors.forEach(error => {
              this.addNotify({text: error, error: true});
            })
          }

          throw new Error(serverError);
        }
        else {
          if (response.data.data.created_ids) {
            this.updatePositionIds(response.data.data.created_ids);
            if (response.data.data.created > 0)
              this.addNotify({text: this.t('notifications.rowsAdded', { count: response.data.data.created })});
          }
          if (response.data.data.updated > 0)
            this.addNotify({text: this.t('notifications.rowsUpdated', { count: response.data.data.updated })});

          if (Array.isArray(response.data.data.positions) && response.data.data.positions.length) {
            await this.applyServerPositions(response.data.data.positions);
          }
        }

        console.log('Данные успешно сохранены');
        this.originalPositions = JSON.parse(JSON.stringify(this.positions));

      } catch (error) {
        console.error('Ошибка при сохранении:', error);
        const message = error?.message || String(error);
        this.addNotify({text: this.t('notifications.saveError', { error: message }), error: true});
      }
      finally {
        this.hideSaving();
      }
    },

    async applyServerPositions(serverPositions) {
      this.isApplyingServerUpdate = true;
      try {
        serverPositions.forEach(serverPos => {
          const idx = this.positions.findIndex(p => String(p.id) === String(serverPos.id));
          if (idx === -1) return;

          // Обновляем серверные поля и значения, пришедшие после импорта.
          [
            'status',
            'arrived',
            'color',
            'article_new',
            'quantity_shipped',
            'quantity_received',
            'storage_shipment',
            'storage_delivery_id',
            'date_order_shipment',
            'date_supplier_order',
            'date_arrival_supplier',
            'date_ready_shipment',
            'date_planned_arrival',
            'comment',
          ].forEach(key => {
            if (Object.prototype.hasOwnProperty.call(serverPos, key)
                && this.positions[idx][key] !== serverPos[key]) {
              this.positions[idx][key] = serverPos[key];
            }
          });
        });
        console.log(this.positions);
        // Дожидаемся, пока watcher отработает на наши мутации (и проигнорирует их по флагу)
        await this.$nextTick();
        // Синхронизируем "оригинал", чтобы getChangedPositions не считал это диффом
        this.originalPositions = JSON.parse(JSON.stringify(this.positions));
      } finally {
        this.isApplyingServerUpdate = false;
      }
    },

    updatePositionIds(createdIds) {
      console.log(createdIds);
      Object.entries(createdIds).forEach(([tempId, realId]) => {
        const index = this.positions.findIndex(p => String(p.id) === String(tempId));
        console.log('index', index);
        if (index !== -1) {
          this.positions[index].id = realId;
          if (this.originalPositions[index]) {
            this.originalPositions[index].id = realId;
          }
        }
      });
    },
    addPositionBelow(index) {
      this.addPosition(index);
    },
    async removePositionFromOrder(position) {
      try {
        const response = await axios.post(`/api/order/positions/remove/${position.id}/`);

        if (response.data.status === 'error') {
          console.error('Ошибка при удалении позиции:', response.data.errors);
          this.addNotify({text: this.t('notifications.deletePositionError'), error: true});
          return;
        }

        const index = this.positions.findIndex(p => p.id === position.id);
        if (index !== -1) {
          this.positions.splice(index, 1);
          this.originalPositions.splice(index, 1);
        }

        console.log('Позиция успешно удалена');
        this.addNotify({text: this.t('notifications.deletePositionSuccess')});

        this.showModalRemove = false;
        this.removePosition = null;

      } catch (error) {
        console.error('Ошибка при удалении позиции:', error);
      }
    },

    toggleRowSelection(positionId, event) {
      const isChecked = event.target.checked;

      if (isChecked) {
        if (!this.selectedPositions.includes(positionId)) {
          this.selectedPositions.push(positionId);
        }
      } else {
        const index = this.selectedPositions.indexOf(positionId);
        if (index > -1) {
          this.selectedPositions.splice(index, 1);
        }
      }
    },

    toggleSelectAll(event) {
      const isChecked = event.target.checked;

      if (isChecked) {
        this.selectedPositions = this.filteredPositions.map(p => p.id);
      } else {
        this.selectedPositions = [];
      }
    },

    isPositionSelected(positionId) {
      return this.selectedPositions.includes(positionId);
    },

    isAllSelected() {
      return this.filteredPositions.length > 0 &&
          this.selectedPositions.length === this.filteredPositions.length;
    },

    async applyBulkEdit() {
      if (!this.hasSelectedPositions) {
        return;
      }

      const updates = {};

      Object.entries(this.bulkEditData).forEach(([key, value]) => {
        if (key !== 'date_planned_arrival' && value !== null && value !== undefined && value !== '') {
          updates[key] = value;
        }
      });

      if (Object.keys(updates).length === 0) {
        console.log('Нет изменений для применения');
        this.closeEditModal();
        return;
      }

      this.selectedPositions.forEach(positionId => {
        const position = this.positions.find(p => p.id === positionId);
        if (position) {
          Object.assign(position, updates);
          if (Object.prototype.hasOwnProperty.call(updates, 'date_supplier_order')
              || Object.prototype.hasOwnProperty.call(updates, 'contract_term')) {
            this.recalculatePlannedArrival(position);
          }
        }
      });

      console.log('Массовые изменения применены к', this.selectedPositions.length, 'позициям');

      this.closeEditModal();
    },

    closeEditModal() {
      this.showEdit = false;
      this.resetBulkEditData();
    },

    resetBulkEditData() {
      this.bulkEditData = {
        storage_shipment: null,
        storage_delivery_id: null,
        quantity_ordered: null,
        quantity_received: null,
        quantity_shipped: null,
        status: null,
        date_order_shipment: null,
        date_supplier_order: null,
        date_arrival_supplier: null,
        date_ready_shipment: null,
      };
    },

    pluralizePositions(count) {
      const cases = [2, 0, 1, 1, 1, 2];
      const forms = [
        this.t('plurals.position.one'),
        this.t('plurals.position.few'),
        this.t('plurals.position.many')
      ];
      return forms[(count % 100 > 4 && count % 100 < 20) ? 2 : cases[Math.min(count % 10, 5)]];
    },
  }
}
</script>

<style scoped>
.positions-filters {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.positions-filters > * {
  min-width: 0;
}

.positions-filters :deep(.form-item),
.positions-filters :deep(.dropdown-group),
.positions-filters :deep(.filters-search) {
  min-width: 0;
}

.positions-filters :deep(.positions-filter--arrived .checkbox-label) {
  white-space: nowrap;
}

@media (min-width: 1200px) {
  .positions-filters--all {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .positions-filters--order {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .positions-filters--all .positions-filter--arrived {
    grid-column: span 2;
  }
}

@media (max-width: 1199px) {
  .positions-filters .positions-filter--arrived {
    grid-column: span 2;
  }
}

@media (max-width: 767px) {
  .positions-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .positions-filters .positions-filter--arrived {
    grid-column: span 2;
  }
}

@media (max-width: 479px) {
  .positions-filters {
    grid-template-columns: minmax(0, 1fr);
  }

  .positions-filters .positions-filter--arrived {
    grid-column: span 1;
  }

  .positions-filters :deep(.positions-filter--arrived .checkbox-label) {
    white-space: normal;
  }
}

.allpositions--single-line .table td {
  white-space: nowrap;
}

.allpositions--single-line .table th {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.allpositions--single-line .sort-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  min-width: 0;
  overflow: visible;
  white-space: normal;
}

.allpositions--single-line .sort-icons {
  flex: 0 0 auto;
}

.allpositions--single-line .table-header-text {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.allpositions--single-line .position-cell-text {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.allpositions--single-line .position-cell-text--identifier {
  max-width: none;
  overflow: visible;
  text-overflow: clip;
}

.allpositions--single-line .editable-table td textarea,
.allpositions--single-line .editable-table td input.edit-inp {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.allpositions--single-line .editable-table td textarea {
  overflow-x: hidden;
  overflow-y: hidden;
}

.allpositions--single-line .cell-identifier input.edit-inp {
  overflow: visible;
  text-overflow: clip;
}

.allpositions--single-line :deep(.dropdown__button) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-camera--has-attachments {
  position: relative;
}

.svg-camera-check {
  position: absolute;
  right: -4px;
  top: -4px;
  width: 20px;
  height: 20px;
  color: #1fa463;
  pointer-events: none;
}
/* Keep the native keyboard-accessible clear button visible in editable date cells. */
.cell-calendar :deep(.position-date-input .dp--clear-btn) {
  display: flex;
  right: 26px;
  width: 24px;
  height: 28px;
  padding: 3px;
  align-items: center;
  justify-content: center;
}
.cell-calendar :deep(.position-date-input .dp--clear-btn .dp__input_icons) {
  width: 16px;
  height: 16px;
  padding: 0;
  flex: 0 0 16px;
}
.cell-calendar :deep(.position-date-input .dp--clear-btn:focus-visible) {
  outline: 2px solid var(--biryuzovyy-300);
  outline-offset: 1px;
}
.cell-calendar .calendar-field :deep(.position-date-input .dp__input) {
  padding-left: 8px;
  padding-right: 52px;
}
.cell-calendar:has(.position-date-input) .icon-calendar {
  right: 6px;
}
</style>
